/**
 * pushSubscription.js — the browser's Web Push subscription under the server's CURRENT VAPID key
 * (NotificationContext: on login and when the user enables notifications).
 *
 * A subscription is bound to the VAPID public key it was made with. Once the server signs with another
 * key pair, the push service refuses every send to the old subscription (FCM answers 403) — and the
 * browser keeps handing it back from pushManager.getSubscription(). Why (2026-10-01, VAPID rotation after
 * the breach): the old code reused any existing subscription and re-posted it to /api/push/subscribe,
 * which re-activates the row, so push would have gone silent for every one of the 249 subscriptions
 * (57 people) already on file.
 *
 *   urlBase64ToUint8Array(b64)                     → the key bytes pushManager.subscribe takes
 *   sameServerKey(subscription, keyBytes)          → was this subscription made with that key?
 *   ensurePushSubscription(pushManager, publicKey) → { subscription, replacedEndpoint }
 *       the existing subscription when its key matches, otherwise a fresh one; replacedEndpoint is the
 *       old endpoint (for /api/push/unsubscribe) when one was thrown away, else null
 */

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** A browser that does not expose options.applicationServerKey gives nothing to compare: keep the subscription. */
export function sameServerKey(subscription, keyBytes) {
  const current = subscription && subscription.options && subscription.options.applicationServerKey;
  if (!current) return true;
  const a = new Uint8Array(current);
  if (a.length !== keyBytes.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== keyBytes[i]) return false;
  }
  return true;
}

export async function ensurePushSubscription(pushManager, publicKey) {
  const keyBytes = urlBase64ToUint8Array(publicKey);
  let subscription = await pushManager.getSubscription();
  let replacedEndpoint = null;

  if (subscription && !sameServerKey(subscription, keyBytes)) {
    // subscribe() with a new key throws while the old subscription exists — drop it first
    replacedEndpoint = subscription.endpoint;
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes
    });
  }

  return { subscription, replacedEndpoint };
}
